import { captureException } from '@sentry/react'
import { ErrorKeys } from 'consts/errorKeys'
import slice from 'store/features/aiLibVideo/slice'
import { aiLibSelectors } from 'store/features/aiLibVideo/selectors'
import { projectSelectors } from 'store/features/project/selectors'
import { TAppDispatch, TGetState } from 'store/types'
import { IAiLibVideoListParams } from 'store/types/aiLib'
import { handleApiError } from 'store/utils'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { getAiLibVideos_API, getAllModels_API } from 'api'

const { actions } = slice

const getHistory =
  (params: IAiLibVideoListParams) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const isScoped = aiLibSelectors.isHistoryScopedToCurrentProject(state)
    const currentProjectId = projectSelectors.id(state)

    if (isScoped && !currentProjectId) {
      dispatch(actions.setHistoryList([]))
      dispatch(actions.setHistoryTotalCount(0))
      dispatch(actions.setIsLoaded(true))
      dispatch(actions.setIsInitialLoading(false))
      return
    }

    const isLoadMore = params.offset !== undefined && params.offset > 0

    const isAlreadyLoading = isLoadMore
      ? aiLibSelectors.isLoadingMore(state)
      : aiLibSelectors.isInitialLoading(state)

    if (isAlreadyLoading) return

    if (isLoadMore) {
      dispatch(actions.setIsLoadingMore(true))
    } else {
      dispatch(actions.setIsInitialLoading(true))
    }

    const activeFilters = aiLibSelectors.activeFilters(state)

    try {
      const response = await getAiLibVideos_API({
        ...params,
        videoProjectId: isScoped ? Number(currentProjectId) : undefined,
        ...activeFilters
      })

      if (isLoadMore) {
        dispatch(actions.appendHistoryList(response.data))
      } else {
        dispatch(actions.setHistoryList(response.data))
        dispatch(actions.setIsLoaded(true))
      }
      dispatch(actions.setHistoryTotalCount(response.totalCount!))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      if (isLoadMore) {
        dispatch(actions.setIsLoadingMore(false))
      } else {
        dispatch(actions.setIsInitialLoading(false))
      }
    }
  }

const changeModel =
  (modelCode: string | null) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const duration = aiLibSelectors.selectedDuration(state)
    const aspectRatio = aiLibSelectors.selectedAspectRatio(state)
    const quality = aiLibSelectors.selectedQuality(state)

    const model = modelCode
      ? (aiLibSelectors.allModels(state).find(m => m.model === modelCode) ??
        null)
      : null

    dispatch(
      actions.setFilter({
        modelCode,
        duration:
          !model ||
          duration === null ||
          model.options.some(o => o.duration === duration)
            ? duration
            : null,
        aspectRatio:
          !model ||
          aspectRatio === null ||
          model.capabilities.aspectRatios?.includes(String(aspectRatio))
            ? aspectRatio
            : null,
        quality:
          !model ||
          quality === null ||
          model.options.some(o => o.qualities.some(q => q.quality === quality))
            ? quality
            : null
      })
    )
  }

// Fetches the full, un-origin'd models catalog (avatar included) once and caches
// it in redux. Re-entrant calls are skipped while a request is in flight or after
// data has loaded, so re-opening the AI History tab doesn't refetch. This backs
// the History filter's Model dropdown + per-clip model-name resolution.
const getAllModels =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    if (
      aiLibSelectors.allModels(state).length ||
      aiLibSelectors.isAllModelsLoading(state)
    ) {
      return
    }

    dispatch(actions.setAllModelsLoading(true))
    try {
      const response = await getAllModels_API()
      dispatch(actions.setAllModels(response.data))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setAllModelsLoading(false))
    }
  }

export const aiLibThunks = {
  ...actions,
  getHistory,
  changeModel,
  getAllModels
}
